import { FormikErrors } from 'formik';
import { i18n as I18n, TFunction } from 'i18next';
import { faUserGroup } from '@fortawesome/free-solid-svg-icons';
import { AnalyticsRowDto } from '@api/models/analytics/AnalyticsRowDto';
import { VisitationAnalyticsGroupBy } from '@api/models/analytics/VisitationAnalyticsGroupBy';
import { VisitationAnalyticsQueryParams } from '@api/params/VisitationAnalyticsQueryParams';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { AnalyticsDomain } from '@api/models/enums/AnalyticsDomain';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { visitationAnalyticsService } from '@api/services/services';
import { AnalyticsExportFormat } from '@api/models/enums/AnalyticsExportFormat';
import { AnalyticsDomainConfig, NO_GROUP_BY } from '../../analyticsDomainConfig';
import { computeDefaultDates, computeTimeBucketDayRange, dayToInstant, formatDateRange } from '../../analyticsDateUtils';
import { VisitationFilterBar } from './VisitationFilterBar';
import { InvitationTypeFilter } from '@api/models/enums/InvitationTypeFilter';
import { VisitationEmployeeRef, VisitationFormValues, VisitationStatus } from './visitationTypes';
import { VisitationDetail } from '../../../visitation/VisitationDetail';

const DEFAULT_FORM_VALUES: VisitationFormValues = {
  entryTimeStart: null,
  entryTimeEnd: null,
  exitTimeStart: null,
  exitTimeEnd: null,
  workplace: [],
  establishment: [],
  supplier: [],
  employee: [],
  activity: [],
  visitedPerson: [],
  status: 'ALL',
  invitationFilter: InvitationTypeFilter.All,
};

const computeDefaultFormValues = (): VisitationFormValues => {
  const dates = computeDefaultDates();
  return {
    ...DEFAULT_FORM_VALUES,
    entryTimeStart: dates.firstOfMonth,
    entryTimeEnd: dates.todayStr,
    exitTimeStart: dates.firstOfMonth,
    exitTimeEnd: dates.todayStr,
  };
};

const DEFAULT_GROUP_PAGE_SIZE = 1000;

const toBaseQueryParams = (v: VisitationFormValues): Omit<VisitationAnalyticsQueryParams, 'groupBy'> => ({
  page: 0,
  size: DEFAULT_GROUP_PAGE_SIZE,
  entryTimeStart: dayToInstant(v.entryTimeStart, 'start'),
  entryTimeEnd: dayToInstant(v.entryTimeEnd, 'end'),
  exitTimeStart: dayToInstant(v.exitTimeStart, 'start'),
  exitTimeEnd: dayToInstant(v.exitTimeEnd, 'end'),
  workplaces: v.workplace.length ? v.workplace.map((w) => w.id) : undefined,
  establishments: v.establishment.length ? v.establishment.map((e) => e.id) : undefined,
  suppliers: v.supplier.length ? v.supplier.map((s) => s.id) : undefined,
  employees: v.employee.length ? v.employee.map((e) => e.id) : undefined,
  activities: v.activity.length ? v.activity.map((a) => a.id) : undefined,
  visitedPersons: v.visitedPerson.length ? v.visitedPerson.map((p) => p.id) : undefined,
  status: v.status === 'ALL' ? undefined : v.status,
  invitationFilter: v.invitationFilter === InvitationTypeFilter.All ? undefined : v.invitationFilter,
});

const toQueryParams = (v: VisitationFormValues, groupBy: string): Record<string, unknown> => ({
  ...toBaseQueryParams(v),
  groupBy: groupBy === NO_GROUP_BY ? undefined : groupBy,
});

const toDrillDownQueryParams = (v: VisitationFormValues, p: { page: number; size: number }): Record<string, unknown> => ({
  ...toBaseQueryParams(v),
  page: p.page,
  size: p.size,
  groupBy: undefined,
});

const applyDrillDown = (formValues: VisitationFormValues, groupBy: string, group: AnalyticsGroupDto): VisitationFormValues | undefined => {
  const id = group.groupKey.id;
  if (!id) return undefined;

  const label = group.groupKey.label || id;
  const synthetic: ReferenceDto = { id, name: label };

  switch (groupBy) {
    case VisitationAnalyticsGroupBy.Workplace:
      return formValues.workplace.some((w) => w.id === id) ? formValues : { ...formValues, workplace: [...formValues.workplace, synthetic] };
    case VisitationAnalyticsGroupBy.Establishment:
      return formValues.establishment.some((e) => e.id === id)
        ? formValues
        : { ...formValues, establishment: [...formValues.establishment, synthetic] };
    case VisitationAnalyticsGroupBy.Supplier:
      return formValues.supplier.some((s) => s.id === id) ? formValues : { ...formValues, supplier: [...formValues.supplier, synthetic] };
    case VisitationAnalyticsGroupBy.SupplierEmployee: {
      const employeeRef: VisitationEmployeeRef = { id, name: label, supplierId: undefined };
      return formValues.employee.some((e) => e.id === id) ? formValues : { ...formValues, employee: [...formValues.employee, employeeRef] };
    }
    case VisitationAnalyticsGroupBy.Activity:
      return formValues.activity.some((a) => a.id === id) ? formValues : { ...formValues, activity: [...formValues.activity, synthetic] };
    case VisitationAnalyticsGroupBy.VisitedPerson:
      return formValues.visitedPerson.some((p) => p.id === id) ? formValues : { ...formValues, visitedPerson: [...formValues.visitedPerson, synthetic] };
    case VisitationAnalyticsGroupBy.Day:
    case VisitationAnalyticsGroupBy.Week:
    case VisitationAnalyticsGroupBy.Month: {
      const range = computeTimeBucketDayRange(group.groupKey, groupBy);
      if (!range) return undefined;
      return { ...formValues, entryTimeStart: range.start, entryTimeEnd: range.end, exitTimeStart: null, exitTimeEnd: null };
    }
    default:
      return undefined;
  }
};

const buildFilterTags = (v: VisitationFormValues, t: TFunction, i18n: I18n, context: { establishmentCount: number }): string[] => {
  const tags: (string | null)[] = [
    formatDateRange(t('analytics.filter.entryTimeLabel'), v.entryTimeStart, v.entryTimeEnd, t, i18n),
    formatDateRange(t('analytics.filter.exitTimeLabel'), v.exitTimeStart, v.exitTimeEnd, t, i18n),
    v.establishment.length > 0 && context.establishmentCount > 1
      ? `${t('analytics.filter.establishments')}: ${v.establishment.map((e) => e.name).join(', ')}`
      : null,
    v.workplace.length > 0 ? `${t('analytics.filter.workplaces')}: ${v.workplace.map((w) => w.name).join(', ')}` : null,
    v.supplier.length > 0 ? `${t('analytics.filter.suppliers')}: ${v.supplier.map((s) => s.name).join(', ')}` : null,
    v.employee.length > 0 ? `${t('analytics.filter.employee')}: ${v.employee.map((e) => e.name).join(', ')}` : null,
    v.activity.length > 0 ? `${t('analytics.filter.activities')}: ${v.activity.map((a) => a.name).join(', ')}` : null,
    v.visitedPerson.length > 0 ? `${t('analytics.filter.visitedPersons')}: ${v.visitedPerson.map((p) => p.name).join(', ')}` : null,
    v.status !== 'ALL' ? `${t('analytics.filter.statusLabel')}: ${t(`analytics.filter.status.${v.status.toLowerCase()}`)}` : null,
    v.invitationFilter === InvitationTypeFilter.OneTimeOnly
      ? `${t('analytics.filter.invitationKindLabel')}: ${t('analytics.filter.oneTimeVisits')}`
      : v.invitationFilter === InvitationTypeFilter.RegularOnly
        ? `${t('analytics.filter.invitationKindLabel')}: ${t('analytics.filter.supplierInvitations')}`
        : null,
  ];
  return tags.filter((tag): tag is string => tag !== null);
};

const toTileFilters = (v: VisitationFormValues): Record<string, unknown> => ({
  entryTimeStart: v.entryTimeStart,
  entryTimeEnd: v.entryTimeEnd,
  exitTimeStart: v.exitTimeStart,
  exitTimeEnd: v.exitTimeEnd,
  workplace: v.workplace,
  establishment: v.establishment,
  supplier: v.supplier,
  employee: v.employee,
  activity: v.activity,
  visitedPerson: v.visitedPerson,
  status: v.status,
  invitationFilter: v.invitationFilter,
});

const URL_KEYS = {
  entryTimeStart: 'ets',
  entryTimeEnd: 'ete',
  exitTimeStart: 'xts',
  exitTimeEnd: 'xte',
  workplace: 'wp',
  establishment: 'est',
  supplier: 'sup',
  employee: 'emp',
  activity: 'act',
  visitedPerson: 'vp',
  status: 'st',
  invitationFilter: 'if',
} as const;

const URL_PARAM_KEYS = Object.values(URL_KEYS);

const toUrlParams = (v: VisitationFormValues): Record<string, string> => {
  const params: Record<string, string> = {};
  if (v.entryTimeStart) params[URL_KEYS.entryTimeStart] = v.entryTimeStart;
  if (v.entryTimeEnd) params[URL_KEYS.entryTimeEnd] = v.entryTimeEnd;
  if (v.exitTimeStart) params[URL_KEYS.exitTimeStart] = v.exitTimeStart;
  if (v.exitTimeEnd) params[URL_KEYS.exitTimeEnd] = v.exitTimeEnd;
  if (v.workplace.length > 0) params[URL_KEYS.workplace] = v.workplace.map((w) => w.id).join(',');
  if (v.establishment.length > 0) params[URL_KEYS.establishment] = v.establishment.map((e) => e.id).join(',');
  if (v.supplier.length > 0) params[URL_KEYS.supplier] = v.supplier.map((s) => s.id).join(',');
  if (v.employee.length > 0) params[URL_KEYS.employee] = v.employee.map((e) => e.id).join(',');
  if (v.activity.length > 0) params[URL_KEYS.activity] = v.activity.map((a) => a.id).join(',');
  if (v.visitedPerson.length > 0) params[URL_KEYS.visitedPerson] = v.visitedPerson.map((p) => p.id).join(',');
  if (v.status !== 'ALL') params[URL_KEYS.status] = v.status;
  if (v.invitationFilter !== InvitationTypeFilter.All) params[URL_KEYS.invitationFilter] = v.invitationFilter;
  return params;
};

const refsFromParam = (params: URLSearchParams, key: string): ReferenceDto[] => {
  const val = params.get(key);
  if (!val) return [];
  return val
    .split(',')
    .filter(Boolean)
    .map((id) => ({ id, name: id }));
};

const VALID_STATUSES: VisitationStatus[] = ['ALL', 'INSIDE', 'LEFT'];
const VALID_INVITATION_FILTERS = Object.values(InvitationTypeFilter);

const fromUrlParams = (params: URLSearchParams): VisitationFormValues => {
  const defaults = computeDefaultFormValues();
  const wp = refsFromParam(params, URL_KEYS.workplace);
  const est = refsFromParam(params, URL_KEYS.establishment);
  const sup = refsFromParam(params, URL_KEYS.supplier);
  const emp: VisitationEmployeeRef[] = refsFromParam(params, URL_KEYS.employee).map((r) => ({
    id: r.id!,
    name: r.name ?? r.id!,
    supplierId: undefined,
  }));
  const act = refsFromParam(params, URL_KEYS.activity);
  const vp = refsFromParam(params, URL_KEYS.visitedPerson);
  const rawStatus = params.get(URL_KEYS.status);
  const status: VisitationStatus =
    rawStatus && VALID_STATUSES.includes(rawStatus as VisitationStatus) ? (rawStatus as VisitationStatus) : defaults.status;
  const rawInvFilter = params.get(URL_KEYS.invitationFilter);
  const invitationFilter: InvitationTypeFilter =
    rawInvFilter && VALID_INVITATION_FILTERS.includes(rawInvFilter as InvitationTypeFilter)
      ? (rawInvFilter as InvitationTypeFilter)
      : defaults.invitationFilter;
  return {
    entryTimeStart: params.get(URL_KEYS.entryTimeStart) ?? defaults.entryTimeStart,
    entryTimeEnd: params.get(URL_KEYS.entryTimeEnd) ?? defaults.entryTimeEnd,
    exitTimeStart: params.get(URL_KEYS.exitTimeStart) ?? defaults.exitTimeStart,
    exitTimeEnd: params.get(URL_KEYS.exitTimeEnd) ?? defaults.exitTimeEnd,
    workplace: wp.length > 0 ? wp : defaults.workplace,
    establishment: est.length > 0 ? est : defaults.establishment,
    supplier: sup.length > 0 ? sup : defaults.supplier,
    employee: emp.length > 0 ? emp : defaults.employee,
    activity: act.length > 0 ? act : defaults.activity,
    visitedPerson: vp.length > 0 ? vp : defaults.visitedPerson,
    status,
    invitationFilter,
  };
};

const validate = (values: VisitationFormValues, t: TFunction): FormikErrors<VisitationFormValues> => {
  const supplierIds = values.supplier.map((s) => s.id!);
  if (supplierIds.length === 0) return {};
  const hasMismatch = values.employee.some((e) => e.supplierId !== undefined && !supplierIds.includes(e.supplierId));
  if (!hasMismatch) return {};
  // Formik typing quirk: FormikErrors<V> for an array field accepts a string
  // (whole-array error) OR per-index entries. The single-string form is what
  // QcsAutocomplete reads via _.get(errors, 'employee'). Because
  // VisitationFormValues extends Args (with a string index signature),
  // FormikErrors's mapped type narrows indexed values to `string | undefined`,
  // disallowing array-field error shapes. The cast through `unknown` is the
  // standard escape hatch.
  return { employee: t('analytics.filter.employeeOutOfScopeWarning') } as unknown as FormikErrors<VisitationFormValues>;
};

const asArray = <T,>(value: unknown, fallback: T[]): T[] => (Array.isArray(value) ? value : fallback);

const isSafeId = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z0-9_-]+$/.test(v);

const fromTileFilters = (filters: Record<string, unknown>): VisitationFormValues => {
  const defaults = computeDefaultFormValues();
  return {
    entryTimeStart: typeof filters.entryTimeStart === 'string' ? filters.entryTimeStart : defaults.entryTimeStart,
    entryTimeEnd: typeof filters.entryTimeEnd === 'string' ? filters.entryTimeEnd : defaults.entryTimeEnd,
    exitTimeStart: typeof filters.exitTimeStart === 'string' ? filters.exitTimeStart : defaults.exitTimeStart,
    exitTimeEnd: typeof filters.exitTimeEnd === 'string' ? filters.exitTimeEnd : defaults.exitTimeEnd,
    workplace: asArray<ReferenceDto>(filters.workplace, defaults.workplace),
    establishment: asArray<ReferenceDto>(filters.establishment, defaults.establishment),
    supplier: asArray<ReferenceDto>(filters.supplier, defaults.supplier),
    employee: asArray<VisitationEmployeeRef>(filters.employee, defaults.employee),
    activity: asArray<ReferenceDto>(filters.activity, defaults.activity),
    visitedPerson: asArray<ReferenceDto>(filters.visitedPerson, defaults.visitedPerson),
    status:
      typeof filters.status === 'string' && VALID_STATUSES.includes(filters.status as VisitationStatus)
        ? (filters.status as VisitationStatus)
        : defaults.status,
    invitationFilter:
      typeof filters.invitationFilter === 'string' && VALID_INVITATION_FILTERS.includes(filters.invitationFilter as InvitationTypeFilter)
        ? (filters.invitationFilter as InvitationTypeFilter)
        : defaults.invitationFilter,
  };
};

export const visitationConfig: AnalyticsDomainConfig<VisitationFormValues> = {
  key: 'visitation',
  domain: AnalyticsDomain.Visitations,
  pageTitleKey: 'analytics.types.visitation.pageTitle',

  card: {
    icon: faUserGroup,
    titleKey: 'analytics.types.visitation.title',
    descriptionKey: 'analytics.types.visitation.description',
  },

  groupByOptions: [
    { value: NO_GROUP_BY, labelKey: 'analytics.groupBy.none' },
    { value: VisitationAnalyticsGroupBy.Workplace, labelKey: 'analytics.groupBy.workplace', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.Establishment, labelKey: 'analytics.groupBy.establishment', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.Supplier, labelKey: 'analytics.groupBy.supplier', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.SupplierEmployee, labelKey: 'analytics.groupBy.supplierEmployee', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.Activity, labelKey: 'analytics.groupBy.activity', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.VisitedPerson, labelKey: 'analytics.groupBy.visitedPerson', group: 'entity' },
    { value: VisitationAnalyticsGroupBy.Hour, labelKey: 'analytics.groupBy.hourOfDay', group: 'time' },
    { value: VisitationAnalyticsGroupBy.Day, labelKey: 'analytics.groupBy.day', group: 'time' },
    { value: VisitationAnalyticsGroupBy.Week, labelKey: 'analytics.groupBy.week', group: 'time' },
    { value: VisitationAnalyticsGroupBy.Month, labelKey: 'analytics.groupBy.month', group: 'time' },
  ],

  groupByTitleKeys: {
    [VisitationAnalyticsGroupBy.Workplace]: 'analytics.dashboard.byWorkplace',
    [VisitationAnalyticsGroupBy.Establishment]: 'analytics.dashboard.byEstablishment',
    [VisitationAnalyticsGroupBy.Supplier]: 'analytics.dashboard.bySupplier',
    [VisitationAnalyticsGroupBy.SupplierEmployee]: 'analytics.dashboard.bySupplierEmployee',
    [VisitationAnalyticsGroupBy.Activity]: 'analytics.dashboard.byActivity',
    [VisitationAnalyticsGroupBy.VisitedPerson]: 'analytics.dashboard.byVisitedPerson',
    [VisitationAnalyticsGroupBy.Hour]: 'analytics.dashboard.byHours',
    [VisitationAnalyticsGroupBy.Day]: 'analytics.dashboard.byDay',
    [VisitationAnalyticsGroupBy.Week]: 'analytics.dashboard.byWeek',
    [VisitationAnalyticsGroupBy.Month]: 'analytics.dashboard.byMonth',
  },

  defaultGroupBy: VisitationAnalyticsGroupBy.Workplace,

  totalsMetrics: {
    count: { labelKey: 'analytics.kpi.total', columnType: 'INT' },
    completedCount: { labelKey: 'analytics.kpi.completed', columnType: 'INT' },
    oneTimeCount: { labelKey: 'analytics.kpi.oneTimeCount', columnType: 'INT' },
    sum: { labelKey: 'analytics.kpi.sumDuration', columnType: 'DURATION_SEC' },
    average: { labelKey: 'analytics.kpi.avgDuration', columnType: 'DURATION_SEC' },
    median: { labelKey: 'analytics.kpi.medianDuration', columnType: 'DURATION_SEC' },
  },

  defaultFormValues: computeDefaultFormValues,
  toQueryParams,
  toDrillDownQueryParams,

  fetchAnalytics: (params) => visitationAnalyticsService.visitationsAnalytics(params as VisitationAnalyticsQueryParams).then((r) => r.data),
  fetchExport: (params) =>
    visitationAnalyticsService.visitationsAnalyticsExport(
      params as VisitationAnalyticsQueryParams & { format?: string; paged?: boolean; columns?: string },
    ),
  exportOptions: [
    { value: AnalyticsExportFormat.Xlsx, labelKey: 'common.exportFormat.xlsx' },
    { value: AnalyticsExportFormat.Pdf, labelKey: 'common.exportFormat.pdf' },
  ],

  applyDrillDown,
  buildFilterTags,

  toTileFilters,
  fromTileFilters,

  toUrlParams,
  urlParamKeys: URL_PARAM_KEYS,
  fromUrlParams,

  validate,

  FilterBarComponent: VisitationFilterBar,

  singleEstablishmentHiddenGroupBys: [VisitationAnalyticsGroupBy.Establishment],

  getRowDrawerId: (row: AnalyticsRowDto): string | undefined => {
    const id = row.values?.id;
    return isSafeId(id) ? id : undefined;
  },
  getRowHref: (row: AnalyticsRowDto): string | undefined => {
    const id = row.values?.id;
    return isSafeId(id) ? `/visitation/${id}` : undefined;
  },
  DrawerBodyComponent: ({ id }) => <VisitationDetail visitationId={id} embedded />,
};
